import React, { useEffect, useState } from "react";
import api, { formatApiError, launchEnrollment } from "@/lib/api";
import { PageHeader, DemoBadge } from "@/components/PageHeader";
import { IntakeDetailDialog } from "@/components/IntakeDetailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Stethoscope, ArrowRight, ArrowLeft, Check, Loader2, CheckCircle2, XCircle,
  ExternalLink, Plus, Compass, ShieldAlert,
} from "lucide-react";

const PRODUCT_TYPES = [
  ["MAPD", "Medicare Advantage (MAPD)"],
  ["PDP", "Prescription Drug (PDP)"],
  ["SNP", "Special Needs Plan (SNP)"],
  ["MEDSUPP", "Medicare Supplement"],
];
const STEPS = ["Consumer", "Coverage & product", "Doctors & meds", "Route & hand-off"];
const EMPTY = {
  consumer_first: "", consumer_last: "", date_of_birth: "", zip_code: "", state: "TX",
  product_type: "MAPD", has_part_a: false, has_part_b: false, existing_plan: "",
  coverage_effective_date: "", doctorsStr: "", medsStr: "", notes: "",
};

export default function MedicareJourney() {
  const [producer, setProducer] = useState(undefined); // undefined=loading, null=none, obj
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [lastIntakeId, setLastIntakeId] = useState(null);

  const loadIntakes = () => api.get("/medicare/intakes").then((r) => setIntakes(r.data));

  useEffect(() => {
    Promise.all([api.get("/advisor/producer"), loadIntakes()])
      .then(([p]) => setProducer(p.data.producer))
      .finally(() => setLoading(false));
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startNew = () => { setForm(EMPTY); setStep(0); setResult(null); setWizard(true); };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        consumer_first: form.consumer_first, consumer_last: form.consumer_last,
        date_of_birth: form.date_of_birth, zip_code: form.zip_code || null, state: form.state,
        product_type: form.product_type, has_part_a: form.has_part_a, has_part_b: form.has_part_b,
        existing_plan: form.existing_plan || null,
        coverage_effective_date: form.coverage_effective_date || null,
        doctors: form.doctorsStr.split(",").map((s) => s.trim()).filter(Boolean),
        medications: form.medsStr.split(",").map((s) => s.trim()).filter(Boolean),
        notes: form.notes || null,
      };
      const { data: intake } = await api.post("/medicare/intakes", payload);
      const { data: route } = await api.post(`/medicare/intakes/${intake.id}/resolve`);
      setResult(route);
      setLastIntakeId(intake.id);
      loadIntakes();
      if (route.resolved) toast.success("Enrollment route resolved");
      else toast.error("No approved route for this context");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resolveExisting = async (intake) => {
    try {
      const { data } = await api.post(`/medicare/intakes/${intake.id}/resolve`);
      loadIntakes();
      if (data.resolved && data.destination?.url) {
        toast.success("Route resolved — opening destination");
        await launchEnrollment(intake.id, data.destination.url);
      } else {
        toast.error(data.reason || "No approved route");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="p-6 sm:p-12 max-w-5xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;

  const canNext = step === 0
    ? form.consumer_first && form.consumer_last && form.date_of_birth && form.state
    : true;

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-5xl mx-auto animate-rise">
      <PageHeader
        eyebrow="Advisor-assisted"
        title="Medicare Journey"
        subtitle="Gather the household's needs, then route the consumer to the correct approved enrollment destination for your producer profile."
        action={!wizard && (
          <Button onClick={startNew} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="mj-start">
            <Plus className="w-4 h-4 mr-1" /> New intake
          </Button>
        )}
      />

      {/* Producer status */}
      {producer === null ? (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-5 mb-6 flex items-start gap-3" data-testid="no-producer-warning">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">No producer profile is linked to your account yet. A platform admin must link a producer (with contracting, state license, and an enrollment destination) before routing will work.</p>
        </div>
      ) : (
        <div className="rounded-xl bg-navy-800 p-5 mb-6 flex items-center gap-3" data-testid="producer-banner">
          <Compass className="w-5 h-5 text-gold shrink-0" />
          <p className="text-sm text-mist-200">
            Routing as producer <span className="text-white font-semibold">{producer.name}</span>
            {producer.npn ? <span className="text-mist-300"> · NPN {producer.npn}</span> : null}. Only destinations you're contracted/licensed for will resolve.
          </p>
        </div>
      )}

      {/* Wizard */}
      {wizard && (
        <div className="bg-white rounded-2xl border border-mist-200 p-8 mb-8">
          <div className="flex items-center gap-2 mb-8">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i < step ? "bg-green-600 text-white" : i === step ? "bg-gold text-navy-900" : "bg-mist-200 text-muted-foreground"}`}>
                    {i < step ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-sm font-semibold hidden sm:block ${i <= step ? "text-navy-900" : "text-muted-foreground"}`}>{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? "bg-green-600" : "bg-mist-200"}`} />}
              </React.Fragment>
            ))}
          </div>

          {step === 0 && (
            <div className="space-y-4" data-testid="mj-step-consumer">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="font-semibold">First name</Label><Input value={form.consumer_first} onChange={upd("consumer_first")} className="mt-1.5" data-testid="mj-first" /></div>
                <div><Label className="font-semibold">Last name</Label><Input value={form.consumer_last} onChange={upd("consumer_last")} className="mt-1.5" data-testid="mj-last" /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label className="font-semibold">Date of birth</Label><Input type="date" value={form.date_of_birth} onChange={upd("date_of_birth")} className="mt-1.5" data-testid="mj-dob" /></div>
                <div><Label className="font-semibold">ZIP</Label><Input value={form.zip_code} onChange={upd("zip_code")} className="mt-1.5" data-testid="mj-zip" /></div>
                <div><Label className="font-semibold">State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} maxLength={2} className="mt-1.5" data-testid="mj-state" /></div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="mj-step-coverage">
              <div>
                <Label className="font-semibold">Product interest</Label>
                <Select value={form.product_type} onValueChange={(v) => setForm({ ...form, product_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="mj-product"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRODUCT_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="mj-parta"><Checkbox checked={form.has_part_a} onCheckedChange={(c) => setForm({ ...form, has_part_a: !!c })} /> <span className="text-sm font-semibold">Has Medicare Part A</span></label>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="mj-partb"><Checkbox checked={form.has_part_b} onCheckedChange={(c) => setForm({ ...form, has_part_b: !!c })} /> <span className="text-sm font-semibold">Has Medicare Part B</span></label>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="font-semibold">Existing plan <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={form.existing_plan} onChange={upd("existing_plan")} className="mt-1.5" data-testid="mj-existing" /></div>
                <div><Label className="font-semibold">Coverage effective date</Label><Input type="date" value={form.coverage_effective_date} onChange={upd("coverage_effective_date")} className="mt-1.5" data-testid="mj-effective" /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="mj-step-doctors">
              <p className="text-sm text-muted-foreground">Captured for the future prefill hand-off — stored securely and not transmitted to any provider yet.</p>
              <div><Label className="font-semibold">Doctors (comma separated)</Label><Input value={form.doctorsStr} onChange={upd("doctorsStr")} className="mt-1.5" placeholder="Dr. Smith, Dr. Lee" data-testid="mj-doctors" /></div>
              <div><Label className="font-semibold">Medications (comma separated)</Label><Input value={form.medsStr} onChange={upd("medsStr")} className="mt-1.5" placeholder="Atorvastatin, Metformin" data-testid="mj-meds" /></div>
              <div><Label className="font-semibold">Notes</Label><Textarea value={form.notes} onChange={upd("notes")} rows={2} className="mt-1.5" data-testid="mj-notes" /></div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="mj-step-route">
              {!result ? (
                <div className="text-center py-6">
                  <h3 className="font-display text-xl font-bold text-navy-900 mb-2">Ready to route</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                    We'll save this intake and resolve the approved enrollment destination for {form.consumer_first || "the consumer"} in {form.state} ({form.product_type}) using your producer profile.
                  </p>
                  <Button onClick={submit} disabled={busy || producer === null} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="mj-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resolve enrollment route"}
                  </Button>
                </div>
              ) : (
                <RouteResult result={result} onDone={() => { setWizard(false); setResult(null); }} onLaunch={() => launchEnrollment(lastIntakeId, result.destination.url)} />
              )}
            </div>
          )}

          {!(step === 3 && result) && (
            <div className="flex justify-between mt-8 pt-6 border-t border-mist-200">
              <Button variant="outline" className="rounded-full" onClick={() => (step === 0 ? setWizard(false) : setStep(step - 1))} data-testid="mj-back">
                {step === 0 ? "Cancel" : (<><ArrowLeft className="w-4 h-4 mr-1" /> Back</>)}
              </Button>
              {step < 3 && (
                <Button disabled={!canNext} onClick={() => setStep(step + 1)} className="bg-navy-800 text-white hover:bg-navy-700 rounded-full font-bold" data-testid="mj-next">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recent intakes */}
      <h3 className="font-display text-xl font-bold text-navy-900 mb-4">Recent Medicare intakes</h3>
      <div className="space-y-3">
        {intakes.length ? intakes.map((it) => (
          <div key={it.id} className="bg-white rounded-xl border border-mist-200 p-5 flex items-center gap-4" data-testid={`intake-row-${it.id}`}>
            <div className="w-11 h-11 rounded-lg bg-mist-100 flex items-center justify-center shrink-0"><Stethoscope className="w-5 h-5 text-navy-800" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-navy-900">{it.consumer_first} {it.consumer_last}</p>
              <p className="text-sm text-muted-foreground">{it.state} · {it.product_type}{it.zip_code ? ` · ${it.zip_code}` : ""}</p>
            </div>
            <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border ${it.status === "routed" ? "bg-green-100 text-green-700 border-green-200" : it.status === "no_route" ? "bg-red-100 text-red-700 border-red-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
              {it.status === "routed" ? "Routed" : it.status === "no_route" ? "No route" : "Intake"}
            </span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setDetailId(it.id)} data-testid={`mj-view-${it.id}`}>
              View
            </Button>
            <Button size="sm" onClick={() => resolveExisting(it)} className="bg-navy-800 text-white hover:bg-navy-700 rounded-full font-bold" data-testid={`mj-resolve-${it.id}`}>
              Resolve & launch
            </Button>
          </div>
        )) : (
          <div className="bg-white rounded-xl border border-dashed border-mist-300 p-12 text-center text-muted-foreground" data-testid="no-intakes">
            No intakes yet. Start a new Medicare intake to route a consumer.
          </div>
        )}
      </div>

      <IntakeDetailDialog open={!!detailId} intakeId={detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}

function RouteResult({ result, onDone, onLaunch }) {
  return (
    <div data-testid="mj-result">
      {result.resolved ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-green-700 font-semibold"><CheckCircle2 className="w-5 h-5" /> Approved enrollment route</div>
          <div className="rounded-lg bg-mist-100 p-4 space-y-2 text-sm">
            <Row label="Provider" value={`${result.provider.name} (${result.provider.integration_type})`} />
            <Row label="Account ref" value={result.profile.provider_account_ref || "—"} />
            <Row label="Contracting" value={result.profile.contracting_status} />
            <Row label="Destination" value={result.destination.label} />
            <Row label="API prefill" value={result.prefill_supported ? "Supported" : "Not yet — consumer completes at provider"} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Destination URL (configuration/data)</p>
            <code className="block text-xs bg-navy-900 text-gold rounded-lg px-3 py-2 break-all">{result.destination.url}</code>
          </div>
          <div className="flex gap-3">
            {result.destination.url && (
              <Button onClick={onLaunch} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="mj-launch">
                Launch enrollment <ExternalLink className="w-4 h-4 ml-1" />
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={onDone} data-testid="mj-done">Done</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-destructive font-semibold"><XCircle className="w-5 h-5" /> No approved route</div>
          <p className="text-sm text-muted-foreground">{result.reason}</p>
          <Button variant="outline" className="rounded-full" onClick={onDone} data-testid="mj-done">Done</Button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-navy-900 text-right">{value}</span>
    </div>
  );
}
